import React from "react";
import {
  SiLeetcode,
  SiHackerrank,
  SiGeeksforgeeks,
  SiCodeforces,
  SiCodechef,
} from "react-icons/si";
import { Card, CardHeader, CardContent } from "@/common/components/ui/card";
import { Badge } from "@/common/components/ui/badge";
import { GoVerified, GoUnverified } from "react-icons/go";
import { Trophy, Code, Brain } from "lucide-react";

const platformStyles = {
  LeetCode: {
    icon: SiLeetcode,
    gradient: "from-orange-50 to-orange-100 border-orange-200",
    iconColor: "text-orange-600",
    stats: [
      { label: "Easy", color: "text-green-600 bg-green-50 border-green-200" },
      {
        label: "Medium",
        color: "text-orange-600 bg-orange-50 border-orange-200",
      },
      { label: "Hard", color: "text-red-600 bg-red-50 border-red-200" },
    ],
  },
  HackerRank: {
    icon: SiHackerrank,
    gradient: "from-green-50 to-green-100 border-green-200",
    iconColor: "text-green-600",
  },
  GeeksforGeeks: {
    icon: SiGeeksforgeeks,
    gradient: "from-emerald-50 to-emerald-100 border-emerald-200",
    iconColor: "text-emerald-600",
  },
  Codeforces: {
    icon: SiCodeforces,
    gradient: "from-blue-50 to-blue-100 border-blue-200",
    iconColor: "text-blue-600",
  },
  CodeChef: {
    icon: SiCodechef,
    gradient: "from-purple-50 to-purple-100 border-purple-200",
    iconColor: "text-purple-600",
  },
};

const PlatformCard = ({ platform, data, style }) => {
  const Icon = style.icon;

  return (
    <Card
      className={`group transition-all duration-300 hover:shadow-lg bg-gradient-to-br ${style.gradient}`}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center space-x-2">
          <Icon className={`w-6 h-6 ${style.iconColor}`} />
          <h3 className="font-semibold text-lg">{platform}</h3>
        </div>
        {data.verified ? (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200 flex gap-1 items-center"
          >
            <GoVerified className="w-3 h-3" /> Verified
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="bg-gray-50 text-gray-700 border-gray-200 flex gap-1 items-center"
          >
            <GoUnverified className="w-3 h-3" /> Unverified
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          {platform === "LeetCode" && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border bg-green-50 border-green-200 p-2 text-center">
                  <p className="text-xs text-gray-600">Easy</p>
                  <p className="font-semibold text-green-600">
                    {data["Easy count"]}
                  </p>
                </div>
                <div className="rounded-lg border bg-orange-50 border-orange-200 p-2 text-center">
                  <p className="text-xs text-gray-600">Medium</p>
                  <p className="font-semibold text-orange-600">
                    {data["Medium count"]}
                  </p>
                </div>
                <div className="rounded-lg border bg-red-50 border-red-200 p-2 text-center">
                  <p className="text-xs text-gray-600">Hard</p>
                  <p className="font-semibold text-red-600">
                    {data["Hard count"]}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <Code className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-600">Total Solved</span>
                </div>
                <span className="font-semibold">{data["All count"]}</span>
              </div>{" "}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-gray-600">Contests</span>
                </div>
                <span className="font-semibold">
                  {data.attendedContestsCount}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-600">Username</span>
                </div>
                <span className="font-semibold">{data.username}</span>
              </div>
            </>
          )}{" "}
          {platform === "HackerRank" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Total Score</p>
                  <p className="font-semibold text-lg">{data.total_score}</p>
                </div>
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Algorithm Score</p>
                  <p className="font-semibold text-lg">
                    {data.algorithms_score}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Data Structures</p>
                  <p className="font-semibold text-lg">
                    {data.structures_score}
                  </p>
                </div>
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Mathematics</p>
                  <p className="font-semibold text-lg">
                    {data.mathematics_score}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-600">Username</span>
                </div>
                <span className="font-semibold">{data.username}</span>
              </div>
            </>
          )}{" "}
          {platform === "GeeksforGeeks" && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border bg-green-50 border-green-200 p-2 text-center">
                  <p className="text-xs text-gray-600">Easy</p>
                  <p className="font-semibold text-green-600">
                    {data.EasyCount}
                  </p>
                </div>
                <div className="rounded-lg border bg-orange-50 border-orange-200 p-2 text-center">
                  <p className="text-xs text-gray-600">Medium</p>
                  <p className="font-semibold text-orange-600">
                    {data.MediumCount}
                  </p>
                </div>
                <div className="rounded-lg border bg-red-50 border-red-200 p-2 text-center">
                  <p className="text-xs text-gray-600">Hard</p>
                  <p className="font-semibold text-red-600">{data.HardCount}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border bg-blue-50 border-blue-200 p-2 text-center">
                  <p className="text-xs text-gray-600">School</p>
                  <p className="font-semibold text-blue-600">
                    {data.SchoolCount}
                  </p>
                </div>
                <div className="rounded-lg border bg-purple-50 border-purple-200 p-2 text-center">
                  <p className="text-xs text-gray-600">Basic</p>
                  <p className="font-semibold text-purple-600">
                    {data.BasicCount}
                  </p>
                </div>
                <div className="rounded-lg border bg-indigo-50 border-indigo-200 p-2 text-center">
                  <p className="text-xs text-gray-600">All</p>
                  <p className="font-semibold text-indigo-600">
                    {data.allCount}
                  </p>
                </div>
              </div>{" "}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Code className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm text-gray-600">
                    Problems Submitted
                  </span>
                </div>
                <span className="font-semibold">{data.problemSubmitted}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-600">Username</span>
                </div>
                <span className="font-semibold">{data.username}</span>
              </div>
            </>
          )}{" "}
          {platform === "Codeforces" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Current Rating</p>
                  <p className="font-semibold text-lg">
                    {data.contestRating || 0}
                  </p>
                </div>
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Max Rating</p>
                  <p className="font-semibold text-lg">{data.maxRating || 0}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Total Problems</p>
                  <p className="font-semibold text-lg">{data.total || 0}</p>
                </div>
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Contests</p>
                  <p className="font-semibold text-lg">
                    {data.attendedContestsCount || 0}
                  </p>
                </div>
              </div>{" "}
              <div className="rounded-lg border bg-white/50 p-3 text-center">
                <p className="text-sm text-gray-600 mb-1">Max Rank</p>
                <p className="font-semibold text-lg">
                  {data.maxUserRank || "Not Ranked"}
                </p>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-600">Username</span>
                </div>
                <span className="font-semibold">{data.username}</span>
              </div>
            </div>
          )}
          {platform === "CodeChef" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Current Rating</p>
                  <p className="font-semibold text-lg">{data.rating || 0}</p>
                </div>
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Max Rating</p>
                  <p className="font-semibold text-lg">{data.maxRating || 0}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Total Solved</p>
                  <p className="font-semibold text-lg">
                    {data.totalProblemsSolved || 0}
                  </p>
                </div>
                <div className="rounded-lg border bg-white/50 p-3">
                  <p className="text-sm text-gray-600 mb-1">Contests</p>
                  <p className="font-semibold text-lg">
                    {data.contestsParticipated || 0}
                  </p>
                </div>
              </div>{" "}
              <div className="rounded-lg border bg-white/50 p-3 text-center">
                <p className="text-sm text-gray-600 mb-1">Stars</p>
                <p className="font-semibold text-lg">{data.star || 0}⭐</p>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-600">Username</span>
                </div>
                <span className="font-semibold">{data.username}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const PlatformPerformance = ({ platformData }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
      <PlatformCard
        platform="LeetCode"
        data={platformData.leetcode}
        style={platformStyles.LeetCode}
      />
      <PlatformCard
        platform="HackerRank"
        data={platformData.hackerrank}
        style={platformStyles.HackerRank}
      />
      <PlatformCard
        platform="GeeksforGeeks"
        data={platformData.gfg}
        style={platformStyles.GeeksforGeeks}
      />
      <PlatformCard
        platform="Codeforces"
        data={platformData.codeforces}
        style={platformStyles.Codeforces}
      />
      <PlatformCard
        platform="CodeChef"
        data={platformData.codechef}
        style={platformStyles.CodeChef}
      />
    </div>
  );
};

export default PlatformPerformance;
